import { EpocusBrandLockup } from "$components/epocus-logomark";
import { ErrorBoundary } from "$components/error-boundary";
import { ErrorState } from "$components/error-state";
import { LoadingPage } from "$components/loading/loading-page";
import { ConfigProvider } from "$global-providers/config-provider/provider";
import { GlobalProviders } from "$global-providers/global-providers";
import { DevToolsOverlay } from "$routes/_lib/devtools-overlay/dev-tools-overlay";
import { Auth0CallbackPage } from "$routes/auth0/callback/page";
import { RedirectToAuth0Login } from "$routes/login/page";
import { SignOut } from "$routes/sign-out/page";
import { UnauthorizedPage } from "$routes/unauthorized/page";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import GlobalAppIssueOverlay from "./global-providers/global-app-issue-overlay/global-app-issue-overlay";
import "$routes/_lib/validation/configure-yup";
import "$routes/_lib/polyfills/core-js";
import useLoadPolyfillsEffect from "$routes/_lib/polyfills/use-load-polyfills-effect";
import { RequireAuth } from "$routes/_lib/require-auth";
import { OfflineRoute } from "$routes/offline/routes";
import { EpocusRoutes } from "./routes/epocus/routes";

const ContextFreeErrorPage: FC<{
  details?: { name: string; message: string; stack?: string };
}> = ({ details }) => (
  // Plain-string translations keep this renderable above the intl/MUI providers.
  <ErrorState
    variant="boundary"
    details={details}
    title="Something went wrong"
    retryLabel="Try again"
    contactSupport="If this persists, please report this to the ePOCUS team with a photo of the below:"
  />
);

const PrivateProviders: FCwC = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (!isLoading && !isAuthenticated) {
    // If we do not have a session token at this point,
    // the user probably tried to go directly to a private route.
    // Clear any user state and redirct to the login page
    return <SignOut />;
  }

  return (
    <>
      <DevToolsOverlay />
      {children}
      <GlobalAppIssueOverlay />
    </>
  );
};

const App: FC = () => {
  const polyfillsLoaded = useLoadPolyfillsEffect();

  React.useEffect(() => {
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  if (!polyfillsLoaded) {
    return null;
  }

  return (
    <ErrorBoundary
      name="root"
      // We must override the default ErrorPage so we don't use any contexts we don't have access to.
      fallback={(error) => (
        <ContextFreeErrorPage
          details={{
            name: error.error.name,
            message: error.error.message,
            stack: error.componentStack,
          }}
        />
      )}
    >
      <ConfigProvider
        configServicePath={CONFIG_SERVICE_PATH}
        errorElement={(error) => (
          <ContextFreeErrorPage
            details={{
              name: error.name,
              message: error.message,
              stack: error.stack,
            }}
          />
        )}
        loadingElement={
          <LoadingPage
            // We must override the logo and translations so we don't use any contexts we don't have access to.
            logomark={<EpocusBrandLockup />}
            translations={{ loadingTip: "Loading..." }}
          />
        }
      >
        <RouterProvider
          router={Sentry.wrapCreateBrowserRouter(createBrowserRouter)(
            createRoutesFromElements(
              <Route element={<GlobalProviders />}>
                <Route index element={<Navigate to="/epocus" replace />} />
                <Route path="login" element={<RedirectToAuth0Login />} />
                <Route path="offline/*" element={<OfflineRoute />} />
                <Route path="sign-out" element={<SignOut />} />
                <Route path="auth0">
                  <Route path="callback" element={<Auth0CallbackPage />} />
                </Route>
                <Route path="unauthorized" element={<UnauthorizedPage />} />
                <Route
                  path="epocus/*"
                  element={
                    <RequireAuth>
                      <PrivateProviders>
                        <EpocusRoutes />
                      </PrivateProviders>
                    </RequireAuth>
                  }
                />
                <Route path="*" element={<Navigate to="/epocus" replace />} />
              </Route>,
            ),
          )}
        />
      </ConfigProvider>
    </ErrorBoundary>
  );
};

ReactDOM.createRoot(document.getElementById("app")).render(<App />);
